/** @module csi/html/Widget */
define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/fx",
	"dojo/_base/lang",
	"dojo/aspect",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/dom-style",
	"dojo/query",
	"dojo/store/Memory",
	"dojo/when",
	"dijit/registry",
	"csi",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/comm",
	"csi/def",
	"csi/def/Heading",
	"csi/heading/_base",
	"csi/html",
	"csi/html/ViewRef",
	"csi/prefs",
	"csi/service/imageService",
	"csi/state",
	"csi/view",
	"csi/view/_CustomViewMixin",
	"csi/view/Widget",
	"dojo/text!csi/templates/html.html",

	"dijit/layout/ContentPane",
	"csi/html/ToolbarPluginToolbars"
], function(array, declare, fx, lang, aspect, domClass, domConstruct, domStyle, query, Memory, when, registry,
	csi, Deferred, _, comm, csiDef, Heading, csiHeading, html, ViewRef, csiPrefs, {waitForImagesToLoad}, state, csiView, _CustomViewMixin, Widget, template) {

	return declare([Widget, _CustomViewMixin], {
		// base widget members
		flexHeightPropagetesToChildren: false,
		splitterType: "composite",
		templateString: template,

		// String: toolbar plugins configuration
		plugins: ['print', 'pdf', 'refresh', '|', 'htmlToolbars'],
		pluginsRight: ['print', 'pdf'],

		// DOMNode: connection point for content pane containing template
		contentNode: null,
		fadeNode: null,

		// String: html file to load into view
		htmlTemplate: "",

		// Boolean: indicates if default custom view for nested views should be the site default custom view rather than user/role default
		//prefNestedSiteDefaults: false,

		// Boolean: indicates if nested custom views can have node context that should be preserved - by default the view strips the
		// node context so that they can be reused for nodes with different contexts
		//prefNestedNodeContext: false,

		// Boolean: indicates whether the view should be automatically scaled to the print size
		printAutoScale: true,

		// Function[]: functions to process after initial load - function can return a deferred object to delay execution of next function in sequence
		//_deferredFunctions: null,

		doesQueryTotalExceedMax: function() {
			// summary: check child views if they exceed the max
			if (!this._started) {
				return true;
			}

			var b = false;

			this.traverseViews(function(v) {
				if (v.bViewed && v.doesQueryTotalExceedMax()) {
					b = true;
				}
			});

			return b;
		},

		getAction: function(idAction, element) {
			// summary: also look for actions on defs
			var action = this.inherited(arguments);
			if (!action) {
				array.some(this.defView.m_defs, function(obj) {
					action = obj.getAction ? obj.getAction(idAction) : null;
					return action != null;
				});
			}

			return action;
		},

		getContentRoot: function() {
			return this.contentNode ? this.contentNode.domNode : null;
		},

		getFadeNode: function() {
			// summary: by default fade content pane
			return this.getContentRoot();
		},

		getNestedViewStore: function() {
			// summary: return store for nested non-slim views
			var data;

			if (this._started) {
				// annotate view with sort order so that views are listed in same order as they appear in DOM
				query("." + html.CSS_VIEW, this.domNode).forEach(function(domNode, i) {
					var v = registry.byNode(domNode);
					if (v) {
						v._htmlSortOrder = i;
					}
				});

				var views = csi.sort(this.getNestedViews(), "_htmlSortOrder");

				// convert to data for store
				data = _.map(views, function(v) {
					var context = v.getViewPreferenceContext();
					var id = this.prefNestedNodeContext ? context : csiPrefs.stripNodeContextFromKey(context);

					return {
						context,
						contextUsed: v.prefViewContextUsed,
						id,
						name: v.get("compositeCustomViewName"),
						store: v.get("customViewStore"),
						view: "",
						visible: false
					};
				}, this);
			} else {
				// compose value from ViewRef defs with node instance - mainly for custom view migration tool
				var defView = this.defView;
				var rcView = defView.getRootClass();
				var context = defView.getId();

				var addItem = function(viewId, nodeType) {
					data.push({id: csiPrefs.composeKey(csiDef.composeViewKey(viewId, nodeType || "Unknown"), context)});
				};

				data = [];

				_.each(defView.m_defs, function(obj) {
					if (obj instanceof ViewRef) {
						// it is possible to get "Unknown" type even when valid because we are working without
						// a node instance which could be derived from referenced class.
						var rc = obj.getReferencedClass(rcView);
						var type = rc && csiDef.getClassViewDef(rc, obj.m_id, false, true);

						// it is possible that view is on derived class of rc, so use base class
						if (!type && rc?.abstract) {
							_.each(rc.getConcreteClasses(), function(rcConcrete) {
								if (type = csiDef.getClassViewDef(rcConcrete, obj.m_id, false, true)) {
									addItem(obj.m_id, type);
								}
							});
						} else {
							addItem(obj.m_id, type);
						}
					}
				});
			}

			return new Memory({data: data || []});
		},

		getNestedViews: function() {
			// summary: return list of views nested under this one that are included in custom view
			var views = [];

			this.traverseViews(function(v) {
				if (!v.defView.m_bSlim) {
					views.push(v);
				}
			});

			return views;
		},

		getNestedViewsValue: function(/*Store?*/store) {
			// summary: return value for default custom view for nested views
			var defaults = csiPrefs.getDefaults(this.prefNestedSiteDefaults);
			var value = {};

			store = store || this.getNestedViewStore();
			store.query().forEach(function(obj) {
				var x;

				if (!obj.store) {
					x = csi.EMPTY_URL;
				} else if (obj.contextUsed) {
					x = defaults.get(obj.contextUsed);
				} else {
					x = defaults.get(obj.context);
				}

				value[obj.id] = x;
			});

			return value;
		},

		getPrintProperties: function() {
			// summary: return object containing properties from view necessary to recreate it for printing that are not custom view options
			var params = {};
			var oldValue = this.nestedViews;
			if (oldValue) {
				// update nested views value with current view selections
				var newValue = params.nestedViews = _.clone(oldValue);
				var viewMap = csi.hashArray(this.getNestedViews(), function(view) {
					return view.getViewPreferenceContext();
				});

				_.each(this.nestedViews, function(value, key) {
					// if view exists but is not started, then do not include in printout (it is only a placeholder for custom view editing)
					var view = viewMap[key];
					if (view) {
						newValue[key] = view.isStarted() ? (view.get("customViewId") || csi.EMPTY_URL) : null;
					}
				});
			}

			return params;
		},

		hasContent: function() {
			// summary: return true if there are child Views, or Headings that have content
			var rootNode = this.getContentRoot();
			if (!rootNode) {
				return false;
			}

			// if there is at least one child view, then return true
			var b;

			this.traverseViews(function(v) {
				b = true;
			});

			if (b) {
				return true;
			}

			// if there is at least one Heading with non-empty content, then return true
			var nl = html.queryDom([html.getDataAttributeSelector(html.DATA_HEADING)], rootNode);

			return _.some(nl, function(domNode) {
				return !html.isEmpty(domNode);
			});
		},

		/**
		* Return true if view contains multiple child views
		* @return {boolean}
		*/
		isStackedView: function() {
			var views = array.filter(csiView.getViews(), function(v) {
				return v.viewParent == this;
			}, this);

			return (views.length >= 2);
		},

		setNestedViewDefault: function(/*csi.view.Widget*/view, /*csi.html.ViewRef*/viewRef, /*Context*/ctx) {
			// summary: set default custom view for nested view if necessary - return false if view is not included in composite custom view
			if (view.defView.m_bSlim) {
				return true;
			}

			if (view.prefViewContext == null) {
				if (this.supportsCustomViews()) {
					// nested view should have additional preference context if in composite view
					view.prefViewContext = this.defView.getId();
				} else {
					// pre 4.9.5, context was set for ViewRefs inside any Html view - now we only set
					// context if composite view; still need to check if Site Default is stored with old context
					view.prefViewContextOld = this.defView.getId();
				}
			}

			var context = view.getViewPreferenceContext();
			var nestedViews = this.nestedViews;
			if (nestedViews) {
				// determine initial custom view for nested view
				var id = this.prefNestedNodeContext ? context : csiPrefs.stripNodeContextFromKey(context);
				var urlView = nestedViews[id];

				if (urlView) {
					// use custom view specified for nested view unless it is the default
					view.urlDefaultView = urlView;
				} else {
					// view is not included in current composite view
					return false;
				}
			} else if (this.prefNestedSiteDefaults) {
				// if using site default views for composite view, then set default custom view for nested view to site default
				view.urlDefaultView = csiPrefs.getDefaults(true).get(context);
			}

			return true;
		},

		/**
		 * @override
		 * Also track all headings used in view which includes any fixed keys
		 */
		_resolveAttributeSpecs() {
			this.inherited(arguments);

			const {defView, specBuilder} = this;
			const {defsByKey, m_headings} = defView;
			const setKeys = new Set();
			const a = [];

			m_headings?.forEach((h) => {
				setKeys.add(h.getKey());
			});

			// TODO: use defView.getFixedHeadings()? Do we need ALL headings, or just those being displayed in view?
			_.each(defsByKey, (obj, key) => {
				if ((obj instanceof Heading) && !setKeys.has(obj.getKey())) {
					// resolve heading if not already done
					specBuilder.getSpecForHeading(obj);
					a.push(obj);
				}
			});

			defView.allHeadings = m_headings?.concat(a) ?? a;
		},

		/**
		 * Iterate through child views and call same function on each
		 */
		onViewPrint: function() {
			this.traverseViews(function(view) {
				view.onViewPrint();
			});
		},

		onViewToolbar: function(/*csi.view.Toolbar*/tb) {
			// summary: extension point for acting on view toolbar
			domClass.add(this.getContentRoot(), "htmlHideToolbars");
		},

		postCreate: function() {
			// summary: load template and other pre-processing
			this.inherited(arguments);

			if (this.contentNode) {
				this.own(
					aspect.after(this.contentNode, "onContentError", lang.hitch(this, this.onViewError), true),
					aspect.after(this.contentNode, "onDownloadError", lang.hitch(this, this.onViewError), true)
				);
			}

			this.headingsNode = this.getContentRoot();
			this.scrollNode = this.scrollNode || this.headingsNode;

			if (state.get("print")) {
				this.fadeNode = null;
			} else if (this.fadeNode = this.getFadeNode()) {
				domStyle.set(this.fadeNode, "opacity", 0);
			}

			if (this.defView.templatePath) {
				this.setTemplate(this.defView.templatePath);
			} else {
				this._onLoadTemplate();
			}
		},

		postMixInProperties: function() {
			// summary: only allow preferences if print view
			if ((this.defView.getType() == "Html") && !this.bPrintView) {
				this.prefClass = null;
			}

			this.inherited(arguments);
		},

		queryForKey: function(/*String*/key) {
			// summary: return NodeList that matches given key
			return query(html.getDataAttributeSelector(html.DATA_TEMPLATE_KEY, key), this.getContentRoot());
		},

		/**
		 * Set content for pane
		 * @param {string} value - HTML content for view
		 */
		setContent: function(value) {
			const {contentNode} = this;

			if (contentNode) {
				contentNode.set("content", value);

				// fade in content if content did not load initially
				this._processFade();
			}
		},

		setTemplate: function(/*String*/s) {
			// summary: set template file with filename relative to dojo (Module/html/A.html) or WebAccess (Html/B.html) folder
			if (s) {
				this.htmlTemplate = csi.urlForModule(s);
				when(this.contentNode.set("href", this.htmlTemplate), lang.hitch(this, this._onLoadTemplate));
			}
		},

		_hasToolbar: function() {
			// summary: show toolbar only if explicitly defined
			// FIX: currently Slim="1" is used for print views, so override base behavior
			return this.defView.defToolbar;
		},

		_onLoadTemplate: function() {
			// summary: extensions point to process template
		},

		_onResize: function() {
			// summary: resize content
			if (html.exists(this.contentNode)) {
				this.contentNode.resize();
			}
		},

		_onRootDependencyPaths: function(/*String[]*/paths) {
			// summary: add own paths - Headings and Images are already taken care of, so skip them
			if (this.isShowingRootNode()) {
				_.each(this.defView.m_defs, function(def) {
					var p = def.getQueryPath?.() || def.m_path;
					if (p) {
						paths.push(p);
					}
				});
			}
		},

		_onRootRequest: function(/*Request*/request) {
			// summary: add URLs from ViewRefs
			if (this.isShowingRootNode()) {
				_.each(this.defView.m_defs, function(def) {
					var url = def.isViewRef && def.getValidURL();
					if (url) {
						request.addParameter(comm.QUERY_URL, url, true);
					}
				});
			}
		},

		_redraw: function() {
			// summary: reload data and template
			var self = this;
			var contentNode = this.contentNode;
			var fadeNode = this.fadeNode;
			var dfd;

			if (fadeNode) {
				// Move content out of content pane and fade out. Destroy widgets now to
				// clean up views but preserve dom for fading out. This is important even for
				// tab handling so location keys are correct (Tab vs Tab1 vs Tab2).
				var fadeOutNode = domConstruct.create("div", {"class": fadeNode.className});
				query("> *", fadeNode).wrapAll(fadeOutNode);
				domConstruct.place(fadeOutNode, fadeNode, "before");

				// use height:0 rather than display:none so that width is valid when building
				// child views such as property views which can have a dynamic number of columns
				domStyle.set(fadeNode, {
					height: 0,
					opacity: 0,
					overflowY: "hidden"
				});

				dfd = new Deferred();

				fx.fadeOut({
					node: fadeOutNode,
					onEnd: function() {
						html.destroyElement(this.node);
						dfd.resolve();
					}
				}).play();
			}

			this.when(dfd, function() {
				// once view faded-out, then refresh view template, if any, and redraw
				if (contentNode && contentNode.get("href")) {
					self.when(contentNode.refresh(), function() {
						self._onLoadTemplate();
					});
				}

				Widget.prototype._redraw.call(self);
			});
		},

		_buildView: function() {
			// summary: if we have gotten here, then view data is loaded; make sure
			// the template is loaded before continuing with rendering
			if (this.contentNode) {
				when(this.contentNode.onLoadDeferred, lang.hitch(this, this._buildHtml));
			} else {
				this._postView();
			}
		},

		_buildHtml: function() {
			// summary: render view after template and data loaded
			if (this.get("contentMessage")) {
				this._buildContentMessage();
				this._processFade();
				this._endView();
				return;
			}

			var ctx = this.getContext();
			var domNode = this.getContentRoot();

			this._deferredFunctions = [];

			// find all keys and fill with associated resources
			this._processHtml(ctx, domNode);

			// load delayed elements (i.e. referenced views)
			this.when(this._processDeferreds(), lang.hitch(this, function() {
				this._processFade();
				this._postView();
			}));
		},

		_processDeferreds: function() {
			// summary: process deferred resources until none left
			var a = this._deferredFunctions;
			if (!a || !a.length) {
				return waitForImagesToLoad();
			}

			var dfd = new Deferred();

			function _onError(err) {
				dfd.reject(err);
			}

			function _onImages(err) {
				dfd.resolve();
			}

			function _processOne() {
				var f = a.shift();
				if (f) {
					when(f(), _processOne, _onError);
				} else {
					// once all deferreds (i.e. nested views) are loaded, then wait for all images to load before continuing
					waitForImagesToLoad().then(_onImages, _onError);
				}
			};

			_processOne();

			return dfd;
		},

		/**
		 * Render resource in view
		 * @param {Context} ctx 
		 * @param {object} resource 
		 * @param {HTMLElement} element 
		 * @return {Promise<void> | undefined}
		 */
		_processDefinition(ctx, resource, element) {
			if (resource instanceof Heading) {
				csiHeading.toHtml(ctx, resource, element);
			} else {
				const res = resource.keyToHtml(ctx, element);
				if (res) {
					this._deferredFunctions.push(res);
					return res;
				}
			}
		},

		_processFade: function() {
			// summary: fade in view
			if (this.fadeNode) {
				// reset styles for ContentPane to prepare for fade-in
				domStyle.set(this.fadeNode, {
					"height": "",
					"overflowY": ""
				});

				// resize now that child views are visible
				this.resize();

				// fade in new view
				fx.fadeIn({node: this.fadeNode}).play();
			}
		},

		_processHtml: function(/*Context*/ctx, /*DOMNode*/domNode) {
			// summary: process each element with a key according to its matching definition
			if (!domNode) {
				return;
			}

			var defs = this.defView.defsByKey || {};
			var repeats = {};

			repeats[html.DATA_TEMPLATE_KEY_REPEAT] = _.filter(this.defView.m_headings, function(h) {
				return !h.bFixedKey;
			});

			// store order of keys to be used for custom views
			this._keyOrder = html.getDataAttributeValues(html.DATA_TEMPLATE_KEY, domNode);

			// process keys found in template
			html.templateToHtml(ctx, domNode, lang.delegate(defs, repeats), lang.hitch(this, this._processKey));
		},

		_processKey: function(/*Context*/ctx, /*String*/key, /*Object*/def, /*DOMNode*/domNode) {
			// summary: render definition in context for element
			if (!def) return null;

			// if class is defined in HTML, then apply to resource, but do so only once
			// so that if processed multiple times (as in gallery view) it won't be duplicated
			if (!def._bProcKey) {
				var c = html.getClass(domNode);
				if (c) {
					if (def.m_class) {
						def.m_class += ' ' + c;
					} else {
						def.m_class = c;
					}
				}

				def._bProcKey = true;
			}

			return this._processDefinition(ctx, def, domNode);
		},

		_processViewRefForEach: function(/*Context*/ctx, /*Object*/def, /*DOMNode*/domNode, /*Object[]*/viewParams, /*String[]?*/labels) {
			// summary: process key multiple times for each set of view params
			var domBefore = domNode.nextSibling;

			// add label before each table to hold size range name
			if (labels) {
				html.setDataAttribute(domNode, html.DATA_TEMPLATE_LABEL, "");
			}

			var domNodes = _.map(viewParams, function(params, i) {
				// copy and insert before existing dom unless last section
				var domCopy = lang.clone(domNode);
				domNode.parentNode.insertBefore(domCopy, domBefore);

				// set parameters for view
				def.prefViewParams = params;

				// process section
				var res = this._processDefinition(ctx, def, domCopy);
				if (res && labels) {
					html.setInnerText(query(".csi-view-title", domCopy)[0], labels[i]);
				}

				return domCopy;
			}, this);

			// destroy original dom node
			domConstruct.destroy(domNode);

			// reset parameters
			def.prefViewParams = null;

			return domNodes;
		}
	});
});